import httpClient from '@/lib/httpClient'
import { handleFormError } from '@/utils/helpers'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAppStore from '@/store/useAppStore'
import useAuthStore from '@/store/useAuthStore'

export default function useUpdateTeam({
  setError = null,
  clientServerFieldMapping = null,
} = {}) {
  const queryClient = useQueryClient()
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const setNumberOfPeriods = useAppStore((state) => state.setNumberOfPeriods)
  const setPeriodType = useAppStore((state) => state.setPeriodType)

  const action = async ({
    teamId,
    name,
    period_type,
    default_horizon,
    week_starts_on,
    billing,
  }: {
    teamId: number
    name: string
    period_type: string
    default_horizon: number
    week_starts_on: number
    /** Owner only — omitted entirely for other members. */
    billing?: { business_name: string | null; abn: string | null }
  }) => {
    const response = await httpClient.put(
      `/team/team`,
      {
        name,
        period_type,
        default_horizon,
        week_starts_on,
        ...(billing ?? {}),
      },
      {
        headers: {
          'X-Team': teamId,
        },
      },
    )
    return response
  }

  const mutation = useMutation({
    mutationFn: action,
    onSuccess: (res, variables) => {
      toast.success('Space updated')
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      queryClient.invalidateQueries({ queryKey: ['team-details'] })

      // Sync period settings into the app store immediately so the UI reflects
      // the change without requiring a page refresh. Only applies to the active
      // team — updating a background space should not change the current view.
      if (variables.teamId === currentTeamId) {
        setNumberOfPeriods(variables.default_horizon)
        setPeriodType(variables.period_type.toLowerCase())
      }
    },
    onError: (error, variables) =>
      handleFormError({
        error,
        variables,
        setError,
        clientServerFieldMapping,
      }),
  })

  return {
    action: mutation.mutateAsync,
    status: mutation,
  }
}
